import { useCallback, useState } from "react";

import type { LangCode } from "@/lib/gql/__generated__/graphql";

import { Button } from "@/components/ui/button/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog/dialog";
import { InputTextField } from "@/components/ui/form/fields/input-field/input-text";
import { TextAreaField } from "@/components/ui/form/fields/text-area-field";
import Send from "@/components/ui/icon/send";

import type { ComponentProps } from "react";

type Props = {
	label: string;
	dialogTitle?: string;
	textValue: string | undefined;
	textErrorMessage: string | undefined;
	handleChangeText: (input: string) => void;
	sendTestEmail?: ({ lang, email }: { lang: LangCode; email: string }) => Promise<void>;
	lang: LangCode;
	hotelEmailAddress?: string;
	hidePreviewButton?: boolean;
	isOverride?: boolean;
};

export const QuestionEmailTemplateItemWithTextArea = ({
	label,
	dialogTitle,
	textValue,
	textErrorMessage,
	handleChangeText,
	sendTestEmail,
	lang,
	hotelEmailAddress,
	hidePreviewButton = false,
	isOverride = false,
}: Props) => {
	const [open, setOpen] = useState(false);
	const [emailAddress, setEmailAddress] = useState<string>(hotelEmailAddress || "");

	const handleClickPreviewButton = useCallback(() => {
		setOpen(true);
	}, []);

	const handleEmailAddressChange = useCallback((value: string) => {
		setEmailAddress(value);
	}, []);

	const handleSendTestEmail = useCallback(async () => {
		if (sendTestEmail && emailAddress) {
			await sendTestEmail({
				lang,
				email: emailAddress,
			});
		}
		setOpen(false);
	}, [sendTestEmail, emailAddress, lang]);

	const buttonVariant: ComponentProps<typeof Button>["variant"] = emailAddress ? "default" : "disabled";

	return (
		<>
			<div className="space-y-3">
				<div className="flex items-end justify-between">
					<p className="text-sm font-semibold">{label}</p>
					{!hidePreviewButton && isOverride && (
						<Button
							variant={"outline"}
							type={"button"}
							onClick={handleClickPreviewButton}
							icon={<Send size="sm" />}
						>
							テスト送信
						</Button>
					)}
				</div>
				<TextAreaField
					value={textValue ?? undefined}
					handleChange={handleChangeText}
					errorMessage={textErrorMessage}
					placeholder="オリジナルの文面を追加したい場合はこちらに記入をしてください。送られる文面は右上の「テスト送信」から確認できます。"
				/>
			</div>

			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className="space-y-4">
					<DialogHeader>
						<DialogTitle>{dialogTitle || label}に送られるメールのテスト送信</DialogTitle>
						<DialogDescription className="text-xs">
							文言を追加していない場合も、デフォルトのメール内容を確認いただけます
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-8">
						<InputTextField
							label="送信先メールアドレス"
							value={emailAddress}
							handleChange={handleEmailAddressChange}
							errorMessage={undefined}
						/>
						<div className="flex justify-end">
							<Button type="button" size="lg" variant={buttonVariant} onClick={handleSendTestEmail}>
								送信
							</Button>
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</>
	);
};

QuestionEmailTemplateItemWithTextArea.displayName = "QuestionEmailTemplateItemWithTextArea";
